import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import axios from 'helpers/api';
import { mapReleaseNote, parseReleaseNoteBody } from 'data/changelogData';
import ChangelogPopUp from 'components/authentication/ChangelogPopUp';

const initialForm = {
  version: '',
  title: '',
  description: '',
  highlights: ''
};

const getResponsePayload = response => response?.data?.data ?? response?.data;

const getReleaseNotes = response => {
  const payload = getResponsePayload(response);
  if (Array.isArray(payload)) return payload;
  return payload?.items || payload?.release_notes || payload?.rows || [];
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const requiresForce = error => {
  const payload = error?.response?.data;
  return Boolean(payload?.requires_force || payload?.data?.requires_force);
};

const formFromReleaseNote = note => {
  const { description, highlights } = parseReleaseNoteBody(note?.body);
  return {
    version: note?.version || '',
    title: note?.title || '',
    description,
    highlights: highlights.join('\n')
  };
};

const getRequestBody = formData => {
  const highlights = formData.highlights
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => `- ${line.replace(/^[-*\u2022]\s*/, '')}`);

  return {
    version: formData.version.trim(),
    title: formData.title.trim(),
    body: [formData.description.trim(), ...highlights]
      .filter(Boolean)
      .join('\n')
  };
};

const PharmacyChangelogManager = () => {
  const [formData, setFormData] = useState(initialForm);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const loadReleaseNotes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/release-notes/admin/list', {
        params: { limit: 100, offset: 0 }
      });
      setItems(getReleaseNotes(response));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to load release notes.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReleaseNotes();
  }, [loadReleaseNotes]);

  const previewItems = useMemo(() => items.map(mapReleaseNote), [items]);

  const resetForm = () => {
    setFormData(initialForm);
    setEditingId(null);
  };

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(current => ({ ...current, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const payload = getRequestBody(formData);

    if (!payload.version || !payload.title || !payload.body) {
      toast.error('Please fill in version number, title, and description.');
      return;
    }

    setIsSaving(true);
    try {
      if (editingId) {
        try {
          await axios.put(`/release-notes/${editingId}`, payload);
        } catch (error) {
          if (!requiresForce(error)) throw error;

          const shouldForceUpdate = window.confirm(
            'This release note is already published. Users who dismissed it will not see this correction. Update it anyway?'
          );
          if (!shouldForceUpdate) return;

          await axios.put(`/release-notes/${editingId}`, {
            ...payload,
            force: true
          });
        }
        toast.success('Release note updated.');
      } else {
        await axios.post('/release-notes', payload);
        toast.success(
          'Release note saved as a draft. Publish it when it is ready.'
        );
      }

      resetForm();
      await loadReleaseNotes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to save release note.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async id => {
    try {
      const response = await axios.get(`/release-notes/${id}`);
      const releaseNote = getResponsePayload(response);
      setFormData(formFromReleaseNote(releaseNote));
      setEditingId(id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to load this release note.'));
    }
  };

  const handlePublish = async item => {
    if (
      !window.confirm(
        `Publish release note ${item.version}? It will be shown to users on their next login.`
      )
    ) {
      return;
    }

    setPublishingId(item.id);
    try {
      await axios.post(`/release-notes/${item.id}/publish`);
      toast.success(`Release note ${item.version} published.`);
      await loadReleaseNotes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to publish release note.'));
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <>
      <Row className="g-3">
        <Col lg={5}>
          <Card className="h-100">
            <Card.Header className="bg-light d-flex align-items-center justify-content-between">
              <h6 className="mb-0 fw-bold">
                <FontAwesomeIcon
                  icon={editingId ? 'edit' : 'plus-circle'}
                  className="text-primary me-2"
                />
                {editingId ? 'Edit Release Note' : 'Create Release Note Draft'}
              </h6>
              <Badge bg="soft-primary" className="text-primary">
                Pharmacy Admin
              </Badge>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-2">
                  <Form.Label className="required fs-11 fw-semi-bold">
                    Version Number
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    name="version"
                    value={formData.version}
                    onChange={handleChange}
                    placeholder="e.g. 2.9.0"
                    maxLength={32}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label className="required fs-11 fw-semi-bold">
                    Feature / Update Title
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g. Real-Time Dispense Sync"
                    maxLength={255}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label className="required fs-11 fw-semi-bold">
                    Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    size="sm"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe this release and its benefit..."
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fs-11 fw-semi-bold">
                    Key Highlights (optional, one per line)
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    size="sm"
                    name="highlights"
                    value={formData.highlights}
                    onChange={handleChange}
                    placeholder="Faster order search&#10;Fixed the MedPass time-zone bug"
                  />
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    className="flex-1"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <Spinner animation="border" size="sm" className="me-1" />
                    ) : (
                      <FontAwesomeIcon
                        icon={editingId ? 'edit' : 'plus'}
                        className="me-1"
                      />
                    )}
                    {editingId ? 'Save Changes' : 'Save Draft'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={resetForm}
                    disabled={isSaving}
                  >
                    {editingId ? 'Cancel' : 'Clear'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card className="h-100">
            <Card.Header className="bg-light d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h6 className="mb-0 fw-bold">Release Notes ({items.length})</h6>
                <small className="text-muted fs-11">
                  Drafts stay private until explicitly published.
                </small>
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="falcon-primary"
                  size="sm"
                  onClick={() => setShowPreviewModal(true)}
                  disabled={!items.length}
                >
                  <FontAwesomeIcon icon="eye" className="me-1" /> Preview
                  Published Format
                </Button>
                <Button
                  variant="falcon-default"
                  size="sm"
                  onClick={loadReleaseNotes}
                  disabled={isLoading}
                >
                  <FontAwesomeIcon
                    icon="sync-alt"
                    className={isLoading ? 'me-1 fa-spin' : 'me-1'}
                  />{' '}
                  Refresh
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              <div
                className="table-responsive"
                style={{ maxHeight: '420px', overflowY: 'auto' }}
              >
                <Table hover className="align-middle mb-0 fs-11">
                  <thead className="bg-200">
                    <tr>
                      <th style={{ width: '80px' }}>Version</th>
                      <th style={{ width: '90px' }}>Status</th>
                      <th>Title &amp; Summary</th>
                      <th style={{ width: '100px' }}>Published</th>
                      <th style={{ width: '135px' }} className="text-end">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          <Spinner animation="border" size="sm" />
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          No release notes found.
                        </td>
                      </tr>
                    ) : (
                      items.map(item => {
                        const displayItem = mapReleaseNote(item);
                        const isPublished = Boolean(
                          Number(item.is_published) ||
                            item.is_published === true ||
                            item.published_at
                        );
                        return (
                          <tr key={item.id}>
                            <td>
                              <span
                                className="badge px-2 py-1 font-monospace fw-bold"
                                style={{
                                  backgroundColor: '#1e293b',
                                  color: '#ffffff'
                                }}
                              >
                                {displayItem.version}
                              </span>
                            </td>
                            <td>
                              <Badge bg={isPublished ? 'success' : 'secondary'}>
                                {isPublished ? 'Published' : 'Draft'}
                              </Badge>
                            </td>
                            <td>
                              <div className="fw-semi-bold text-900 mb-1">
                                {item.title}
                              </div>
                              <div
                                className="text-600 text-truncate"
                                style={{ maxWidth: '250px' }}
                                title={displayItem.description}
                              >
                                {displayItem.description}
                              </div>
                            </td>
                            <td className="text-muted text-nowrap">
                              {displayItem.date || '—'}
                            </td>
                            <td className="text-end text-nowrap">
                              <Button
                                variant="falcon-default"
                                size="sm"
                                className="btn-sm p-1 fs-11 me-1"
                                onClick={() => handleEdit(item.id)}
                                title="Edit release note"
                              >
                                <FontAwesomeIcon icon="edit" />
                              </Button>
                              {!isPublished && (
                                <Button
                                  variant="falcon-primary"
                                  size="sm"
                                  className="btn-sm p-1 fs-11"
                                  onClick={() => handlePublish(item)}
                                  disabled={publishingId === item.id}
                                  title="Publish release note"
                                >
                                  {publishingId === item.id ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <FontAwesomeIcon icon="paper-plane" />
                                  )}
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ChangelogPopUp
        show={showPreviewModal}
        items={previewItems}
        onClose={() => setShowPreviewModal(false)}
      />
    </>
  );
};

export default PharmacyChangelogManager;
